// client/customer/src/pages/Reserve/by-service/SelectStaff.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronRight, UserRound, Users } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useReserveContext } from '../../../contexts/useReserveContext';
import { fetchStaffOptionsForDate } from '../../../utils/reservation/by-Date/fetchStaffOptionsForDate';
import {
  type StaffForServiceReserve,
} from '../../../utils/reservation/by-service/fetchStaffsForServiceReserve';
import { fetchStaffServiceDurations } from '../../../utils/reservation/by-service/fetchStaffServiceDurations';

// ✅ 共通UI
import ReserveStepHeader from '../ReserveStepHeader';
import StaffSelectionCard from '../StaffSelectionCard';

const ANY_STAFF_LABEL = 'Anyone is okay';

const SelectStaffByService: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditing = searchParams.get('edit') === '1';
  const {
    storeId,
    selectedDate,
    selectedStartTime,
    selectedStaffId,
    isFreeReservation,
    setSelectedStaffId,
    setSelectedStaffName,
    setIsFreeReservation,
    selectedServices,
    setSelectedServices,
  } = useReserveContext();

  const serviceIds = useMemo(
    () =>
      (selectedServices || [])
        .map((s) => Number(s.serviceId))
        .filter((n) => Number.isFinite(n) && n > 0),
    [selectedServices]
  );

  const [rows, setRows] = useState<StaffForServiceReserve[]>([]);
  const [loading, setLoading] = useState(false);
  const [freeAvailable, setFreeAvailable] = useState(false);

  useEffect(() => {
    if (!storeId) {
      toast.info('Please select a store first');
      navigate('/reserve/by-service/store');
      return;
    }
    if (serviceIds.length === 0) {
      toast.info('Please select at least one service');
      navigate('/reserve/by-service/service');
      return;
    }
    if (!selectedDate) {
      toast.info('Please select a date first');
      navigate('/reserve/by-service/date');
      return;
    }
    if (!selectedStartTime) {
      toast.info('Please select a time first');
      navigate(isEditing ? '/reserve/by-service/confirm-reservation' : '/reserve/by-service/time');
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        const data = (
          await fetchStaffOptionsForDate({
            storeId,
            date: selectedDate,
            startTime: selectedStartTime,
            serviceIds,
          })
        );
        setFreeAvailable(data.free_available);
        setRows(data.staffs.map((staff) => ({
          staff_id: staff.staff_id,
          nickname: staff.nickname || staff.name,
          icon_url: staff.icon_url,
          bio: staff.bio,
          role_name: staff.role_name,
          sns_links: staff.sns_links,
        })));
      } catch (e: any) {
        console.error(e);
        toast.error(e?.message || 'Failed to load staff members');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [storeId, selectedDate, selectedStartTime, serviceIds, isEditing, navigate]);

  const handleSelectStaff = async (s: StaffForServiceReserve) => {
    const displayName = s.nickname || '';
    try {
      const durations = await fetchStaffServiceDurations(s.staff_id, serviceIds);
      const durationMap = new Map(
        durations.map((row) => [Number(row.service_id), Number(row.duration_minutes)]),
      );
      setSelectedStaffId(s.staff_id);
      setSelectedStaffName(displayName);
      setSelectedServices(
        selectedServices.map((service) => ({
          ...service,
          duration_minutes:
            durationMap.get(Number(service.serviceId)) ?? Number(service.duration_minutes) ?? 0,
          staffId: s.staff_id,
          staffName: displayName,
        })),
      );
      setIsFreeReservation(false);
      navigate('/reserve/by-service/confirm-reservation');
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to update the selected staff');
    }
  };

  const handleSelectAnyone = () => {
    if (!freeAvailable) return;
    setSelectedServices(
      selectedServices.map((service) => ({
        ...service,
        staffId: null,
        staffName: ANY_STAFF_LABEL,
      })),
    );
    setSelectedStaffId(null);
    setSelectedStaffName(ANY_STAFF_LABEL);
    setIsFreeReservation(true);
    navigate('/reserve/by-service/confirm-reservation');
  };

  return (
    <div className="flex flex-col min-h-[70vh] space-y-4">
      <ReserveStepHeader
        title="Choose your staff"
        onBack={() =>
          navigate(isEditing ? '/reserve/by-service/confirm-reservation' : '/reserve/by-service/time')
        }
        step={5}
        totalSteps={5}
        description={
          isEditing
            ? 'Pick a different staff member while keeping the current services, date, and time.'
            : 'Pick from the staff members available for your selected services, date, and time.'
        }
      />

      {loading ? (
        <p className="text-center text-slate-400 text-sm">Loading staff members...</p>
      ) : rows.length === 0 && !freeAvailable ? (
        isEditing ? (
          <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-rose-100 p-2 text-rose-600">
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-rose-700">Staff cannot be changed</div>
                <div className="mt-1 text-sm leading-6 text-rose-600">
                  The current services, date, and time combination leaves no alternative staff choices.
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center text-slate-400 text-sm">
            There is no available staff for this date, time, and services.
          </p>
        )
      ) : (
        <div className="space-y-3">
          {freeAvailable ? (
            <button
              type="button"
              onClick={handleSelectAnyone}
              className={[
                'w-full rounded-[28px] border px-4 py-4 text-left shadow-sm transition',
                isFreeReservation
                  ? 'border-[#0ABAB5] bg-[linear-gradient(135deg,#0ABAB5,#089d99)] text-white shadow-[0_14px_28px_rgba(10,186,181,0.22)]'
                  : 'border-[#ccefed] bg-[linear-gradient(180deg,rgba(244,253,252,0.98),rgba(255,255,255,0.96))] text-slate-800 hover:border-[#96dfdb] hover:bg-[#f3fbfa]',
              ].join(' ')}
            >
              <div className="flex items-center gap-4">
                <div className={`rounded-3xl p-3 ${isFreeReservation ? 'bg-white/15 text-white' : 'bg-[#dff8f6] text-[#087f7c]'}`}>
                  <Users className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-base font-semibold">{ANY_STAFF_LABEL}</div>
                  <p className={`mt-1 text-xs ${isFreeReservation ? 'text-white/90' : 'text-slate-500'}`}>
                    Book this as a Free reservation and decide the actual staff later.
                  </p>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  {isFreeReservation ? (
                    <UserRound className="h-5 w-5 text-white" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-slate-300" />
                  )}
                </div>
              </div>
            </button>
          ) : null}

          {rows.map((staff) => {
            const displayName = staff.nickname || '';
            const isSelected = !isFreeReservation && selectedStaffId === staff.staff_id;

            return (
              <StaffSelectionCard
                key={staff.staff_id}
                name={displayName || '-'}
                roleName={staff.role_name}
                bio={staff.bio}
                iconUrl={staff.icon_url}
                snsLinks={staff.sns_links}
                isSelected={isSelected}
                onSelect={() => handleSelectStaff(staff)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SelectStaffByService;
